/**
 * The flow copy deck — EVERY customer-visible string in the droom journey,
 * extracted VERBATIM from ~18 screens of JSX literals (DEC-053: extraction
 * before translation; prose written into a component has to be rewritten before
 * it can ever be translated).
 *
 * Rules (documentSteps.ts, ratified by INTAKE-08, carried forward):
 *   • FULL-SENTENCE keys, never fragment joins — Hindi/Arabic word order
 *     differs, so a joined fragment can never translate.
 *   • At most one `{slot}` per key. Two keys with/without a slot beat one key
 *     with an optional fragment ("PAN checked" vs "PAN checked (AB…12)").
 *     The single irreducible exception: reconnect progress carries {step} and
 *     {total} — "step X of Y" cannot be split into two honest sentences.
 *   • `**bold**` markers survive translation (the runtime's rich() renders
 *     them); translators keep the markers around the equivalent words.
 *   • Customer tone: plain, warm, second person, no jargon, no blame, never a
 *     score or a threshold. "Verified/certified" stay RESERVED words.
 *   • MOCK/demo-mode components (MockConsent, MockCapture, dev tools) are NOT
 *     here: they can never render to a real customer, and offering Hindi does
 *     not hide them from anyone real.
 */

export const FLOW_COPY = {
  // ── shared actions & chrome ────────────────────────────────────────────────
  'droom.action.continue': 'Continue',
  'droom.action.tryAgain': 'Try again',
  'droom.action.retake': 'Retake',
  'droom.action.checking': 'Checking…',
  'droom.action.skipStep': 'Skip this step',
  'droom.action.pleaseWait': 'Please wait…',
  'droom.action.copyLink': 'Copy verification link',
  'droom.action.linkCopied': 'Link copied ✓',
  'droom.action.allowed': "I've allowed access",
  'droom.pill.secure': 'Secure',
  'droom.pill.encrypted': 'Encrypted',
  // Not 'Verified': this pill sits on the completion screen, where no verification
  // decision has been made yet. It reports what actually happened — the session was
  // submitted — and the key name is left alone so no call site has to change.
  'droom.pill.verified': 'Submitted',
  'droom.pill.queue': 'Live queue',
  'droom.pill.preparing': 'Preparing',
  // The platform credit at the foot of every screen (owner round 2026-08-28
  // item 4). "KYC Hub" is the brand and stays in Latin in every locale.
  'droom.poweredBy': 'Powered by KYC Hub',
  'droom.error.tryAgain': 'Please try again.',

  // ── app shell (FlowShell) ──────────────────────────────────────────────────
  'droom.shell.preparing': 'Preparing your session…',
  'droom.shell.entryError.title': "We couldn't load your session",
  'droom.shell.entryError.body':
    'Please open the secure invite link we sent you (by SMS or email). If you were already in a call, reopen that same link to rejoin.',
  'droom.shell.entryError.retry': 'Retry',
  // 18-Aug RCA (owner item #2): the frozen verification steps could not be read. We block honestly
  // and offer a retry rather than run a guessed flow that the server would then refuse.
  'droom.shell.planError.title': "We couldn't load your verification steps",
  'droom.shell.planError.body':
    'We could not confirm what this verification needs. Please check your connection and try again — we will not start until we can load the right steps.',
  'droom.shell.planError.retry': 'Retry',

  // ── SCREEN · WELCOME ───────────────────────────────────────────────────────
  'droom.welcome.title': "Let's verify\nyour identity",
  'droom.welcome.sub':
    'A quick, secure video check to finish opening your account. It takes about 5 minutes.',
  'droom.welcome.trust.rbi.l': 'RBI V-CIP',
  'droom.welcome.trust.ifsca.l': 'IFSCA V-CIP',
  'droom.welcome.trust.rbi.s': 'Regulated',
  // VC-01: a session under a tenant's own policy pack — no regulator named, nothing claimed.
  'droom.welcome.trust.tenant.l': 'Live video check',
  'droom.welcome.trust.tenant.s': 'With an officer',
  'droom.welcome.trust.enc.l': 'Encrypted',
  'droom.welcome.trust.enc.s': 'End-to-end',
  'droom.welcome.trust.time.l': '~5 min',
  'droom.welcome.trust.time.s': 'One sitting',
  'droom.welcome.cta': 'Get started',
  'droom.welcome.ref': 'Ref',

  // ── SESSION LANDING (the invite front door) ────────────────────────────────
  'droom.landing.subtitle': 'Video verification',
  'droom.landing.title': 'Your video verification',
  'droom.landing.sub':
    'A short, secure video call with a verification officer to confirm your identity. It takes about five minutes.',
  'droom.landing.need.head': "What you'll need",
  'droom.landing.need.id.t': 'A valid ID document',
  'droom.landing.need.id.d': 'Your original — keep it within reach.',
  'droom.landing.need.cam.t': 'Camera and microphone',
  'droom.landing.need.cam.d': 'Your browser will ask permission — please tap Allow.',
  'droom.landing.need.loc.t': 'Your live location',
  'droom.landing.need.loc.d': 'Captured with your permission when the call starts.',
  'droom.landing.need.place.t': 'A quiet, well-lit place',
  'droom.landing.need.place.d': 'So the officer can see and hear you clearly.',
  'droom.landing.happen.head': 'What will happen',
  'droom.landing.happen.p1':
    "You'll join a **recorded** video call with a verification officer, who will check your ID document and ask you a few questions. With your permission, your live location is captured during the call.",
  'droom.landing.happen.p2':
    "The recording, your location and your responses become part of your verification record, kept as the regulations require. Before anything is collected, you'll be walked through each permission on screen.",
  'droom.landing.band.checking.t': 'Checking your session…',
  'droom.landing.band.checking.b':
    'One moment — confirming this link with your verification provider.',
  'droom.landing.band.preparing.t': 'A few steps before your call',
  'droom.landing.band.preparing.b':
    "Start now — you'll confirm consent and photograph your ID and a selfie, then we'll connect you to a verification officer.",
  'droom.landing.band.joinable.t': 'Ready when you are',
  'droom.landing.band.joinable.b': 'Your verification session is ready to join.',
  'droom.landing.cta.join': 'Join your video call',
  'droom.landing.cta.start': 'Start your verification',
  'droom.landing.session': 'Session',
  'droom.landing.footer':
    'This private link was sent to you by your verification provider — please do not share it.',
  'droom.landing.ended.t': 'This verification session has ended',
  'droom.landing.ended.b':
    "There's nothing more to join here. If you still need to complete your verification, please ask your provider for a fresh link.",
  'droom.landing.notfound.t': "We couldn't find this verification session",
  'droom.landing.notfound.b':
    'Check that you opened the complete link from your invitation — or ask your verification provider to send you a new one.',

  // ── SCREEN · SCHEDULE ──────────────────────────────────────────────────────
  'droom.schedule.subtitle': 'Schedule',
  'droom.schedule.aria': 'Schedule your verification',
  'droom.schedule.checking': 'Checking your appointment…',
  'droom.schedule.booked.title': 'Your appointment',
  'droom.schedule.booked.sub':
    'Times shown in your timezone ({tz}). Joining opens shortly before your slot — you can continue the preparation steps now.',
  'droom.schedule.booked.continue': 'Continue preparation',
  'droom.schedule.booked.move': 'Move this appointment…',
  'droom.schedule.held.title': 'Hold placed',
  'droom.schedule.held.sub': 'This hold lasts about 5 minutes — confirm it to keep the time.',
  'droom.schedule.held.confirm': 'Confirm this time',
  'droom.schedule.held.confirming': 'Confirming…',
  'droom.schedule.held.different': 'Pick a different time',
  'droom.schedule.pick.title': 'Pick a time',
  'droom.schedule.pick.empty': 'No open times in the next 3 days — you can continue now instead.',
  'droom.schedule.pick.now': 'Continue now',
  'droom.schedule.pick.skip': 'Skip — continue now',
  'droom.schedule.moved.title': 'Your session has moved on',
  'droom.schedule.moved.sub': 'Scheduling closed for this session — continue with the next steps.',
  'droom.schedule.choice.title': 'When would you like to verify?',
  'droom.schedule.choice.sub':
    'The video verification takes about 10 minutes. Do it now, or book a time that suits you.',
  'droom.schedule.choice.pick': 'Pick a time instead',
  'droom.schedule.note.taken': 'That time was just taken — pick another.',
  'droom.schedule.note.holdfail': 'Something went wrong holding that time — pick again.',
  'droom.schedule.note.lapsed': 'Your hold lapsed (~5 minutes) — pick a time again.',
  'droom.schedule.note.confirmfail': 'Could not confirm — pick a time again.',
  'droom.schedule.note.repick':
    'Pick your new time — your current slot stays until the new one confirms.',
  'droom.schedule.note.loadfail': 'Could not load times — try again.',

  // ── SCREEN · CONSENT (consent-adjacent — VCIP-REG review set) ──────────────
  'droom.consent.subtitle': 'Video-KYC',
  'droom.consent.title': 'Your privacy, your consent',
  'droom.consent.sub':
    "We'll only use your details to verify you for {regime} Video-KYC. Here's exactly what and why.",
  'droom.consent.process.head': 'What we process',
  'droom.consent.version.title': 'Consent notice version',
  'droom.consent.version.unratified': 'Wording not yet ratified by the compliance team',
  'droom.consent.unratified': 'Notice wording is pending compliance ratification ({version}).',
  'droom.consent.readFull': 'Read the full notice',
  'droom.consent.affirm':
    "I **consent** to KycHub processing my identity data for the purposes above, to complete {tenantName}'s Video-KYC verification.",
  'droom.consent.agree': 'Agree & continue',
  'droom.consent.recording': 'Recording your consent…',
  'droom.consent.recorded': 'Consent recorded ✓',
  'droom.consent.notNow': 'Not now',
  'droom.consent.decline': 'I do not consent',
  'droom.consent.declined.title': 'Consent is needed to continue',
  'droom.consent.declined.body':
    "{tenantName}'s Video-KYC is a regulated verification — we can only proceed with your consent to process your identity data for it. Nothing happens without it, and you can withdraw consent or ask for erasure after the mandatory retention period.",
  'droom.consent.declined.review': 'Review consent again',
  'droom.consent.loading': 'Loading your consent notice…',
  'droom.consent.loadfail': "Couldn't load the consent notice",
  'droom.consent.resolved': 'Consent recorded — continuing…',
  'droom.consent.stampline': 'Timestamped · IP-bound · hash-logged · immutable',
  'droom.consent.purpose.video.t': 'Recorded video call',
  'droom.consent.purpose.video.d':
    'This verification call is recorded and kept as your KYC evidence.',
  'droom.consent.purpose.bio.t': 'Face & liveness',
  'droom.consent.purpose.bio.d':
    'A live check of your face, matched against your identity document.',
  'droom.consent.purpose.loc.t': 'Live location',
  'droom.consent.purpose.loc.d':
    'Captured once to confirm the jurisdiction you are in, as the regulator requires.',
  'droom.consent.purpose.ret.t': 'Data retention',
  'droom.consent.purpose.ret.d':
    'Your identity data is retained for the mandatory period and never sold.',
  'droom.consent.purpose.dedup.t': 'Fraud screening',
  'droom.consent.purpose.dedup.d':
    'To prevent identity fraud, your facial data may be compared against other verification attempts at this institution. It is never shared with other institutions.',

  // ── consent-required interstitials (capture/upload steps) ──────────────────
  'droom.consentreq.title': 'We need your consent first',
  'droom.consentreq.body.docs':
    "Your consent record for this verification is missing, so we can't accept your documents yet. Please review and confirm consent — you'll come straight back here.",
  'droom.consentreq.body.photo':
    "Your consent record for this verification is missing, so we can't accept your photo yet. Please review and confirm consent — you'll come straight back here.",
  'droom.consentreq.review': 'Review consent',

  // ── SCREEN · PAN ───────────────────────────────────────────────────────────
  'droom.pan.subtitle': 'PAN verification',
  'droom.pan.title': 'Your PAN details',
  'droom.pan.sub':
    'We check your PAN with the issuing source before your video call — one less thing to do on camera. Your verification officer confirms the result on the call.',
  'droom.pan.checked.title': 'PAN checked ✓',
  'droom.pan.checked.sub':
    'Checked against the issuing source. Your verification officer will confirm this on the call.',
  'droom.pan.checked.sub.masked':
    'Checked against the issuing source ({masked}). Your verification officer will confirm this on the call.',
  'droom.pan.label.pan': 'PAN (as printed on your card)',
  'droom.pan.label.name': 'Name exactly as on the PAN card',
  'droom.pan.err.format':
    'A PAN has 5 letters, 4 digits, then a letter — like AAAAA9999A. Please check and try again.',
  'droom.pan.err.mismatch':
    "The name didn't match this PAN at the source. Check the spelling against your card — or continue, and your officer will verify it with you on the call.",
  'droom.pan.err.mismatch.score':
    "The name didn't match this PAN at the source (match {score}%). Check the spelling against your card — or continue, and your officer will verify it with you on the call.",
  'droom.pan.err.invalid':
    "The issuing source didn't recognise this PAN. Please re-check the number — or continue, and your officer will verify it with you on the call.",
  'droom.pan.err.provider':
    "We couldn't reach the verification source just now. You can try again — or continue, and your officer will verify your PAN with you on the call.",
  'droom.pan.check': 'Check my PAN',
  'droom.pan.checking': 'Checking…',
  'droom.pan.skip': 'Skip for now — verify on the call instead',
  'droom.pan.fallback': 'Continue — verify my PAN with the officer on the call',
  'droom.pan.privacy':
    'Your PAN is sent once, encrypted, to verify against the issuing source. It is stored as verification evidence under the notice you accepted, and never shown on screen again.',
  // #11 capability honesty: the wire refuses customer PAN capture on this deployment — say so
  // plainly and hand the check to the call, never a form that can only fail.
  'droom.pan.oncall.title': "We'll check your PAN on the call",
  'droom.pan.oncall.body':
    "This step isn't available here right now — your verification officer will check your PAN with you on the video call. Nothing is lost, and there's nothing more to do here.",

  // ── SCREEN · AADHAAR (DigiLocker) ──────────────────────────────────────────
  'droom.aadhaar.subtitle': 'Aadhaar verification',
  'droom.aadhaar.aria': 'Aadhaar verification via DigiLocker',
  'droom.aadhaar.relaying': 'Finishing your Aadhaar verification…',
  'droom.aadhaar.checking': 'Checking your verification status…',
  'droom.aadhaar.verified.title': 'Aadhaar verified ✓',
  'droom.aadhaar.verified.sub':
    'Your Aadhaar was verified through DigiLocker. Nothing else to do here.',
  'droom.aadhaar.unavailable.title': 'Not available right now',
  'droom.aadhaar.unavailable.detail':
    "Aadhaar via DigiLocker isn't available right now. Continue — your documents will be verified with you on the call.",
  'droom.aadhaar.fallback.cta': 'Continue — verify with me on the call',
  'droom.aadhaar.fallback.retry': 'Try DigiLocker again',
  'droom.aadhaar.fallback.error': "We couldn't confirm this just now — please try again in a moment.",
  'droom.aadhaar.fallback.refused':
    "We couldn't move this to the call — DigiLocker appears to be working, so please try it again.",
  'droom.aadhaar.failed.expired': 'That DigiLocker attempt expired — please start it again.',
  'droom.aadhaar.failed.title': "That didn't complete",
  'droom.aadhaar.failed.incomplete': 'The verification did not complete.',
  'droom.aadhaar.failed.relay':
    'Could not finish the verification — you can retry, or continue without it.',
  'droom.aadhaar.failed.state':
    "This step can't run right now. Try again — or your officer will verify your document with you on the call.",
  'droom.aadhaar.failed.start': 'Could not start the DigiLocker journey — retry, or continue without it.',
  'droom.aadhaar.offer.title': 'Verify your Aadhaar with DigiLocker',
  'droom.aadhaar.offer.body':
    "You'll be taken to the government's DigiLocker service to sign in with your Aadhaar and OTP. It happens on their site — we never see your Aadhaar number, and only the verification result comes back here.",
  'droom.aadhaar.label.name': 'Your name (as on Aadhaar)',
  'droom.aadhaar.placeholder.name': 'Name',
  'droom.aadhaar.label.mobile': 'Mobile number',
  'droom.aadhaar.placeholder.mobile': '10-digit mobile',
  'droom.aadhaar.err.mobile':
    'Enter a valid Indian mobile number (it should start with 6, 7, 8, or 9).',
  'droom.aadhaar.err.form': 'Please check your mobile number and try again.',
  'droom.aadhaar.hint':
    'Your name and mobile only start the DigiLocker sign-in — your verified details come back from DigiLocker itself.',
  'droom.aadhaar.cta': 'Verify with DigiLocker',
  'droom.aadhaar.starting': 'Starting…',
  'droom.aadhaar.skip': 'Skip — verify documents on the call instead',
  'droom.aadhaar.without': 'Continue without it',

  // ── SCREEN · CAPTURE (legacy self-capture pair) ────────────────────────────
  'droom.capture.subtitle': 'Step 2 · Capture',
  'droom.capture.doc.label': 'ID document',
  'droom.capture.doc.title': 'Photograph your ID document',
  'droom.capture.doc.sub':
    'Lay the document flat, fill the frame, and avoid glare — every detail should be readable.',
  'droom.capture.selfie.label': 'Selfie',
  'droom.capture.selfie.title': 'Now a clear selfie',
  'droom.capture.selfie.sub':
    'Face the camera in even light and keep your whole face inside the guide.',
  'droom.capture.loading.title': 'Getting your capture ready',
  'droom.capture.loading.sub': 'Checking what you may have already uploaded.',
  'droom.capture.snag': 'We hit a snag',
  'droom.capture.complete': 'Captures complete',
  'droom.capture.queue.entering': 'Adding you to the verifier queue…',
  'droom.capture.queue.failed': 'We could not add you to the queue — please try again.',
  'droom.capture.moment': 'One moment…',
  'droom.capture.err.link':
    'This verification link is not valid any more — please use the latest link you were sent.',
  'droom.capture.err.list': 'We could not check your captured documents. Please try again.',
  'droom.capture.err.queue': 'We could not add you to the queue. Please check your connection and try again.',
  'droom.capture.flip': 'Switch camera',
  'droom.capture.badge.rear': 'REAR CAMERA',
  'droom.capture.badge.front': 'FRONT CAMERA',
  'droom.capture.badge.camera': 'CAMERA',
  'droom.capture.badge.preview': 'PREVIEW',
  'droom.capture.retakeThis': 'Please retake this photo.',
  'droom.capture.primer.rear': "We'll use your rear camera to photograph your document.",
  'droom.capture.hint.face': 'Keep your whole face inside the guide',
  'droom.capture.hint.doc': 'Fill the frame with your document · avoid glare',
  'droom.capture.session.checking': 'Checking your session…',
  'droom.capture.session.loadfail': 'Could not load',
  'droom.capture.use': 'Use this photo',
  'droom.capture.turnon': 'Turn on camera',
  'droom.capture.shoot': 'Capture photo',
  'droom.capture.assessing.short': 'Checking quality…',
  'droom.capture.camera.startingBtn': 'Starting camera…',
  'droom.capture.camera.checkingBtn': 'Checking camera…',
  'droom.capture.queue.enteringBtn': 'Entering the queue…',
  'droom.capture.tile.uploadedEarlier': 'Uploaded earlier',
  'droom.capture.tile.capturedEarlier': 'Captured earlier',
  'droom.capture.tile.uploading': 'Uploading…',
  'droom.capture.tile.inprogress': 'In progress',
  'droom.capture.tile.next': 'Up next',
  'droom.capture.tile.hash': 'Server-side integrity hash (first 12 characters)',
  'droom.capture.footer': 'Quality is checked on your device before anything uploads',
  'droom.capture.previewAlt': 'Preview of your captured photo',
  // Item 7: the pinned docType names the document (falls back to the generic title).
  'droom.capture.doc.titleTyped': 'Photograph your {doc}',

  // ── selfie-family check steps (config strings) ─────────────────────────────
  'droom.selfiestep.subtitle': 'Selfie',
  'droom.selfiestep.tile': 'Selfie',
  'droom.selfiedocstep.subtitle': 'Photo with your document',
  'droom.selfiedocstep.tile': 'You + document',
  'droom.selfiecore.loading.title': 'Getting your camera ready',
  'droom.selfiecore.loading.sub': 'Checking whether this photo is already done.',
  'droom.selfiecore.notavailable': 'Not available here yet',

  // ── SCREEN · DOCUMENT UPLOAD ───────────────────────────────────────────────
  'droom.uploadstep.subtitle': 'Documents',
  'droom.uploadstep.none':
    "There's nothing for you to upload here — your documents are checked with your verifier on the call.",
  'droom.uploadstep.notyet': 'Not uploaded yet',
  'droom.uploadstep.oncall': 'Handled on your call',
  'droom.uploadstep.lockedCta': 'Upload your {doc} to continue',
  'droom.uploadstep.uploadingAria': 'Uploading your {doc}',
  'droom.uploadstep.footer': 'Files are checked on your device before anything uploads',
  'droom.doctype.PAN_CARD': 'PAN card',
  'droom.doctype.AADHAAR': 'Aadhaar',
  'droom.doctype.DRIVING_LICENCE': 'Driving licence',
  'droom.doctype.VOTER_ID': 'Voter ID',
  'droom.doctype.PASSPORT': 'Passport',
  'droom.doctype.UTILITY_BILL': 'Utility bill',
  'droom.doctype.SELFIE': 'Selfie',
  'droom.doctype.SELFIE_WITH_DOC': 'Selfie with your document',
  'droom.doctype.OTHER_DOCUMENT': 'Supporting document',
  'droom.doctype.fallback': 'Document',

  // ── stepper step labels (item 7 — the current step, named) ─────────────────
  'droom.step.consent': 'Consent',
  'droom.step.capture': 'Documents & selfie',
  'droom.step.confirm': 'Confirm your details',
  'droom.step.waiting': 'Waiting room',
  'droom.step.call': 'Live video call',
  'droom.step.complete': 'All done',

  // ── plan checks with no dedicated screen (MissingCheckStep) ────────────────
  'droom.check.DIGILOCKER_AADHAAR': 'Aadhaar (DigiLocker)',
  'droom.check.PAN_SOURCE': 'PAN verification',
  'droom.check.DOC_CAPTURE': 'Document capture',
  'droom.check.CONSENT': 'Consent',
  'droom.check.FACE_MATCH': 'Face match',
  'droom.check.LIVENESS': 'Liveness',
  'droom.check.GEO_IP': 'Location check',
  'droom.check.AGENT_QUESTIONS': 'Agent questions',
  'droom.check.AML_SCREENING': 'Screening',
  'droom.check.SELFIE': 'Selfie',
  'droom.check.SELFIE_WITH_DOC': 'Photo with your document',
  'droom.missing.aria': '{label} — handled on the call',
  'droom.missing.body':
    'This step is completed with your verification agent on the live call — nothing to do here now. Continue to the next step.',

  // ── SCREEN · WAITING ROOM ──────────────────────────────────────────────────
  'droom.wait.subtitle': 'Waiting room',
  'droom.wait.officerJoined': 'Your verification officer has joined — connecting you now',
  'droom.wait.finding': 'Finding your place in line…',
  'droom.wait.next': "You're next — we'll connect you the moment an officer is free",
  'droom.wait.position': "You're {position} in line for the next verifier",
  'droom.wait.eta': '~{min} min estimated wait',
  'droom.wait.progressAria': 'Queue progress',
  'droom.wait.keepOpen': 'Keep this screen open',
  'droom.wait.autoconnect': "We'll connect you when an officer joins",
  'droom.wait.spotSaved': 'Your spot is saved',
  'droom.wait.reschedule': 'Reschedule for later',
  'droom.wait.rescheduling': 'Opening the schedule…',
  // Item 8b: the be's honest queue refusal — never the eternal skeleton.
  'droom.wait.refusal.title': "We can't add you to the queue yet",
  'droom.wait.refusal.body': 'Something from an earlier step still needs to be completed:',
  'droom.wait.refusal.goback': 'Go back and complete it',

  // ── SCREEN · SCHEDULED ARRIVAL (CD32) ──────────────────────────────────────
  // Two screens, not two error messages: a customer who is EARLY has done nothing
  // wrong, and one who is LATE has missed an appointment — an ordinary human event
  // with an ordinary remedy. Neither is told off, and neither loses a completed step.
  'droom.arrival.subtitle': 'Your appointment',
  'droom.arrival.title': 'Your verification is scheduled',
  'droom.arrival.ist': '{time} IST',
  'droom.arrival.body':
    'You are early — nothing is wrong. We will bring you into the queue automatically when your appointment time arrives.',
  'droom.arrival.countdown': 'Opens in {time}',
  'droom.arrival.countdownAria': 'Time until your appointment opens',
  'droom.arrival.autoconnect': 'We join you automatically',
  'droom.arrival.stepsSaved': 'Your completed steps are saved',
  'droom.arrival.missed.title': 'Your scheduled window has passed',
  'droom.arrival.missed.was': 'Your appointment was {time}.',
  'droom.arrival.missed.body':
    'We could not connect you in that window, so a new time is needed. Everything you have already completed stays saved.',
  'droom.arrival.missed.cta': 'Choose a new time',

  // ── gates on the call flow (geo / permissions) ─────────────────────────────
  'droom.live.subtitle': 'Live verification',
  'droom.geo.explain.title': 'Share your live location',
  'droom.geo.explain.body':
    "This verification includes your live location — your institution's process confirms where you are during the video call, in line with RBI V-CIP guidance. It's captured once, with your permission, and stored as part of your verification record.",
  'droom.geo.explain.body.tenant':
    "This verification includes your live location — your institution's process confirms the country you are calling from during the video call. It's captured once, with your permission, and stored as part of your verification record.",
  'droom.geo.explain.body.ifsca':
    "This verification includes your live location — your institution's process confirms the jurisdiction you are calling from during the video call, in line with IFSCA KYC guidance. It's captured once, with your permission, and stored as part of your verification record.",
  'droom.geo.explain.cta': 'Share my location',
  'droom.geo.captured.title': 'Location recorded',
  'droom.geo.getting.title': 'Getting your location…',
  'droom.geo.captured.body': 'Thank you — connecting you to your verification officer.',
  'droom.geo.getting.body': 'If your browser asks, please tap Allow.',
  'droom.geo.denied.title': 'Location permission was denied',
  'droom.geo.denied.body':
    'The call can still go ahead — your verification officer will see that location evidence is missing and may ask about it. To share it, allow location for this site in your browser settings, then try again.',
  // DEC-060(A): concrete how-to beside the honest state — most denials are an inadvertent tap on
  // "Block", and the officer on the call will be reading the same guidance from their side.
  'droom.geo.denied.howto':
    'How to allow it: tap the padlock (or ⓘ) beside the address bar → Permissions → Location → Allow, then tap Try again. On iPhone also check Settings → Privacy & Security → Location Services → your browser → While Using the App. If location is off for the whole device, switch it on in your quick settings first.',
  'droom.geo.without': 'Continue without location',
  'droom.geo.postfailed.title': "Your location couldn't be recorded",
  'droom.geo.degraded.title': "We couldn't get your location",
  'droom.geo.degraded.fallback': 'Something went wrong while getting your location.',
  'droom.geo.degraded.body':
    'You can try again, or continue — your officer will see that location evidence is missing.',

  'droom.perm.checking': 'Checking your camera and microphone…',
  'droom.perm.primer.title': 'Turn on your camera & microphone',
  'droom.perm.primer.body':
    'Your camera and microphone are needed for the live verification call with your officer. Your browser will ask next — please tap **Allow**.',
  'droom.perm.requesting.title': 'Waiting for your permission…',
  'droom.perm.requesting.body': 'If your browser is asking, please tap **Allow**.',
  'droom.perm.denied.title': 'Camera & microphone are blocked',
  'droom.perm.denied.body':
    'Your browser is blocking access, and only you can turn it back on — no website can re-ask for you.',
  'droom.perm.osdenied.title': 'Allow camera access in your system settings',
  'droom.perm.osdenied.body':
    "Your browser couldn't show the prompt — access is turned off at the system level. You'll need to change this yourself.",
  'droom.perm.steps.aria': 'How to allow access',
  'droom.perm.autoresume': "We'll reconnect automatically the moment you allow it.",
  'droom.perm.nodevice.title': 'No camera or microphone found',
  'droom.perm.nodevice.fallback': 'This device has no camera or microphone available for the call.',
  'droom.perm.nodevice.advice': 'Please use a device with a working camera and microphone.',
  'droom.perm.webview.title': 'Open this link in your browser',
  'droom.perm.webview.body':
    "You've opened this from inside another app, which can't reliably use your camera. Please open this link in **Chrome** or **Safari** to continue your verification.",
  'droom.perm.webview.tip': 'Tip: tap the ••• menu and choose "Open in browser".',
  'droom.perm.unsupported.title': "This browser can't run the call",
  'droom.perm.unsupported.body':
    "Your browser doesn't support live camera access. Please open this link in an up-to-date Chrome or Safari — on iPhone that means iOS 14.3 or later; on Android, Chrome 90 or later.",

  // Testmap #12 (owner mandate): BELOW-FLOOR devices get an honest advisory naming the minimum —
  // never a silent failure blamed on the customer. Advisory only: an old device that works is
  // not refused.
  'droom.perm.osnotice.ios':
    "Your iPhone's software is older than this call supports ({min} or later). You can still try, but if the camera fails, please update your iPhone in Settings → General → Software Update and reopen this link.",
  'droom.perm.osnotice.android':
    'Your browser is older than this call supports ({min} or later). You can still try, but if the camera fails, please update Chrome from the Play Store and reopen this link.',
  'droom.perm.osnotice.samsung':
    'Your browser is older than this call supports ({min} or later). You can still try, but if the camera fails, please update Samsung Internet — or open this link in Chrome — and try again.',

  // recovery step-lists (useMediaPermissions) — one key per numbered line
  'droom.perm.rec.os.1':
    'Your browser could not show the permission prompt — the block is at the system level.',
  'droom.perm.rec.os.2':
    'Open your device Settings → Privacy → Camera (and Microphone) and allow access for your browser.',
  'droom.perm.rec.os.3': 'Return here and tap "I\'ve allowed access".',
  'droom.perm.rec.chrome.1': 'Tap the camera / lock icon at the left of the address bar.',
  'droom.perm.rec.chrome.2': 'Set Camera and Microphone to Allow.',
  'droom.perm.rec.chrome.3': 'Tap "I\'ve allowed access" (no need to reload).',
  'droom.perm.rec.firefox.1': 'Tap the lock icon in the address bar, then Connection settings / Permissions.',
  'droom.perm.rec.firefox.2': 'Clear the blocked Camera and Microphone entries and set them to Allow.',
  'droom.perm.rec.firefox.3': 'Tap "I\'ve allowed access".',
  'droom.perm.rec.safari.1': 'Open Safari → Settings for this website (or aA menu → Website Settings).',
  'droom.perm.rec.safari.2': 'Set Camera and Microphone to Allow, then reload if prompted.',
  'droom.perm.rec.safari.3': 'Tap "I\'ve allowed access".',
  'droom.perm.rec.generic.1': "Open your browser's site settings for this page.",
  'droom.perm.rec.generic.2': 'Set Camera and Microphone to Allow.',
  'droom.perm.rec.generic.3': 'Tap "I\'ve allowed access".',

  // blocked-camera card (capture steps)
  'droom.cam.denied.title': 'Camera access is blocked',
  'droom.cam.denied.body':
    'Your browser is blocking the camera, and only you can turn it back on — no website can re-ask for you.',
  'droom.cam.osdenied.title': 'Allow camera access in your system settings',
  'droom.cam.osdenied.body':
    "Your browser couldn't show the permission prompt — access is turned off at the system level. You'll need to change this yourself.",
  'droom.cam.nodevice.title': 'No camera found',
  'droom.cam.nodevice.fallback':
    'This device has no usable camera. Please continue on a device with a working camera.',
  'droom.cam.webview.title': 'Open this link in your browser',
  'droom.cam.webview.body':
    "You've opened this from inside another app, which can't reliably use your camera. Please open this link in Chrome or Safari to continue.",
  'droom.cam.unsupported.title': "This browser can't take photos",
  'droom.cam.unsupported.body':
    'Live camera access is unavailable here. Please open this link in an up-to-date Chrome or Safari.',
  'droom.cam.nodevice.hookDetail': 'No usable camera was found on this device.',
  'droom.perm.nodevice.hookDetail': 'No camera or microphone was found on this device.',

  // ── SCREEN · LIVE CALL ─────────────────────────────────────────────────────
  'droom.call.connectingNamed': 'Connecting you with {name}…',
  'droom.call.connecting': 'Connecting you with your verifier…',
  // #11 (agent-drop) — the verifier is reconnecting, then (on grace expiry) an apology. Calm,
  // apologetic, never blaming the customer; never says "verified" (nothing was verified).
  'droom.call.verifierAway.title': 'Your verifier is reconnecting',
  'droom.call.verifierAway.body':
    "Please stay on the call — this can take up to a minute. We're sorry for the difficulty.",
  'droom.call.connectingSlow.body':
    'This is taking longer than usual — please stay on this screen.',
  'droom.call.verifierLost.title': 'This call is being ended',
  'droom.call.verifierLost.body':
    'Your verifier will contact you again to restart the verification. We apologise for the inconvenience.',
  'droom.call.verifierLost.support': 'You can close this page now.',
  // #12 (mobile background/phone-call) — the camera paused while away; we are recovering it, and a
  // tap target appears only if automatic recovery needs a gesture.
  'droom.call.cameraLost.body':
    'Your camera paused while you were away — reconnecting it now…',
  'droom.call.resumeMedia.cta': 'Tap to resume camera and sound',
  'droom.call.verifierNamed': '{name} · Verifier',
  'droom.call.verifier': 'Verifier',
  'droom.call.rec': 'REC · ENCRYPTED',
  // DEC-062 C2 / #13: the tile-swap affordance — the customer's own framing is dominant by
  // default. The two directional labels name what tapping WILL show (aria-pressed announces which
  // tile is currently large). The legacy 'droom.call.swapTiles' key is retained for the ledger.
  'droom.call.swapTiles': 'Swap view',
  'droom.call.swapTiles.showVerifier': 'Show verifier large',
  'droom.call.swapTiles.showMe': 'Show me large',
  'droom.call.guidance.label': 'On-screen guidance',
  'droom.call.guidance.text':
    'Keep your face centred and well-lit. Your verifier will guide you through the steps once connected.',
  'droom.call.mute': 'Mute microphone',
  'droom.call.unmute': 'Unmute microphone',
  'droom.call.end': 'End call',
  'droom.call.camOff': 'Turn camera off',
  'droom.call.camOn': 'Turn camera on',
  'droom.call.leave.title': 'Leave the call?',
  'droom.call.leave.body':
    'Your verification is not complete yet. If you leave now, you can rejoin and continue with the same officer.',
  'droom.call.leave.stay': 'Stay on the call',
  'droom.call.leave.leave': 'Leave the call',
  'droom.call.left.title': 'You left the call',
  'droom.call.left.body':
    'Your verification is not complete. Rejoin to continue where you left off — your officer and your progress are kept.',
  'droom.call.left.rejoin': 'Rejoin the call',
  // 2026-08-24: the journey moved to another device. NOT an error, NOT an expiry — the customer is
  // mid-verification on the phone in their hand, and this screen is simply the one they walked away
  // from. It must not send them to support.
  'droom.call.superseded.title': 'You continued on another device',
  'droom.call.superseded.body':
    'Your verification is now open on the other device — carry on there and your progress comes with you. Nothing has been lost, and there is nothing to do on this screen.',
  'droom.call.expired.title': 'This session has ended',
  'droom.call.expired.body':
    'The link may have expired. If your verification was not finished, please use your original link to start again, or contact {tenantName} for a new one.',

  // reconnect overlay
  'droom.rc.lost': 'Connection lost',
  'droom.rc.reconnecting': 'Reconnecting…',
  'droom.rc.sub.suspended':
    "Your progress is saved. When you're ready, rejoin — you'll continue exactly where you left off, with the same verifier.",
  'droom.rc.sub.blip':
    "Your progress is saved. Please stay on this screen — we'll pick up exactly where you left off.",
  'droom.rc.kept': 'STEP {step} OF {total} KEPT',
  'droom.rc.kept.same': 'STEP {step} OF {total} KEPT · SAME VERIFIER',
  'droom.rc.rejoin': 'Rejoin now',

  // ── guided capture overlay (officer-prompted, in-call) ─────────────────────
  'droom.gcap.badge': 'VERIFIER REQUEST',
  'droom.gcap.facing.rear':
    'Your verifier has asked to use your back camera — it photographs documents more clearly. Your call video will switch while you take this photo, and switch back afterwards.',
  'droom.gcap.facing.front':
    'Your verifier has asked to use your front camera for this photo. Your call video will switch while you take it, and switch back afterwards.',
  'droom.gcap.facing.applied.rear':
    'Using your back camera for this photo, as your verifier asked. Your call video switches back when the photo is done.',
  'droom.gcap.facing.applied.front':
    'Using your front camera for this photo, as your verifier asked.',
  'droom.gcap.facing.oneCamera':
    'This device has one camera, so we are using it — you can still take the photo.',
  'droom.gcap.facing.unavailable':
    "We couldn't switch cameras on this device, so we will use the current one. You can still take the photo.",
  'droom.gcap.facing.unconfirmed':
    'Your camera has changed, but this device does not report which one is now in use. Please check the preview below before you take the photo.',
  'droom.gcap.purpose.title':
    'Before we take this photo',
  'droom.gcap.purpose.what':
    'We will take a photograph of your face or your document, from this call.',
  'droom.gcap.purpose.comparedWith':
    'It is compared against your Aadhaar photo and the identity document you provide, to confirm you are who you say you are.',
  'droom.gcap.purpose.processor':
    'It is handled by us and by our verification partner, in India.',
  'droom.gcap.purpose.retention':
    'It is kept as part of your verification record, for as long as the law requires us to hold it.',
  'droom.gcap.purpose.noTraining':
    'It is never used to train or improve any AI model.',
  'droom.gcap.purpose.withdraw':
    'You can stop at any point and speak to your verifier on the call.',
  'droom.gcap.purpose.agree':
    'I understand — continue',
  'droom.gcap.attempts.exhausted':
    'We could not get a clear enough photo. Please speak to your verifier on the call — they will help.',
  'droom.gcap.asked': 'Your verifier asked for this photo on the call.',
  'droom.gcap.doc.title': 'Show your document to the camera',
  'droom.gcap.doc.instruction':
    'Hold the document flat inside the box, close enough that every detail is readable.',
  'droom.gcap.selfiedoc.title': 'Hold your document next to your face',
  'droom.gcap.selfiedoc.instruction':
    'One photo shows you and your document together — face in the oval, document in the box.',
  'droom.gcap.selfie.title': 'Take a live selfie',
  'droom.gcap.selfie.instruction':
    'Look straight into the camera with your whole face in the oval, in good light.',
  'droom.gcap.cat.unclear': 'The copy on file is hard to read, so a clearer photo is needed.',
  'droom.gcap.cat.mismatch': 'Some details need a fresh look against your document.',
  'droom.gcap.cat.tamper': 'Your verifier needs to see the physical document live on camera.',
  'droom.gcap.cat.identity': "Your verifier needs to re-confirm it's you holding your document.",
  'droom.gcap.cat.regulatory': 'This photo is required on the recorded call for your verification.',
  'droom.gcap.cat.flow': 'This photo is part of your verification journey — your verifier will guide you.',
  'droom.gcap.cat.default': 'Your verifier needs this photo to continue your verification.',
  'droom.gcap.done.title': 'Photo sent to your verifier',
  'droom.gcap.done.sub': 'Returning to your call…',
  'droom.gcap.back': 'Back to the call',
  'droom.gcap.send': 'Send this photo',
  'droom.gcap.sending': 'Sending…',
  'droom.gcap.sendingLong': 'Sending to your verifier…',
  'droom.gcap.looksSharp': 'Looks sharp — send it to your verifier.',
  'droom.gcap.checkingQuality': 'Checking the photo quality…',
  'droom.gcap.camOff': 'Your camera is off — turn it back on to take this photo.',
  'droom.gcap.notReady': "The camera isn't ready yet — give it a second and try again.",
  'droom.gcap.uploadFail': "That didn't upload — please try again.",
  'droom.gcap.retryLine': "Let's try that again.",
  'droom.gcap.decline':
    "Not comfortable? Just tell your verifier — they can withdraw this request, and that's recorded too.",
  // V116 / owner item 4 — the officer-triggered shutter, said plainly. A photograph taken with no
  // visible cue is a covert capture whatever the consent record says, so these two lines are part of
  // the feature, not decoration around it: one warns it may happen, the other says it is happening.
  'droom.gcap.remote.mayCapture':
    'Your verifier can take the photo for you while you hold this steady — you will see it happen here.',
  'droom.gcap.remote.firing': 'Your verifier is taking the photo — hold steady',
  // Item 22 — the black viewfinder and the way back. The customer must never be held behind a dead
  // screen with nothing to press; "back to the call" withdraws nothing, it just un-traps them.
  'droom.gcap.preview.dead': 'Your camera preview is not showing — nothing is being captured',
  'droom.gcap.preview.retry': 'Try the camera again',
  'droom.gcap.backToCall': 'Back to the call',
  // Item 9 — the upload fallback, reachable at last. The grade line is not decoration: an uploaded
  // file is weaker evidence than a live capture and the customer is told so plainly.
  'droom.gcap.upload.title': 'Send the document as a file instead',
  'droom.gcap.upload.cta': 'Upload the document instead',
  'droom.gcap.upload.sending': 'Sending your file…',
  'droom.gcap.upload.grade':
    'A photo taken here on the call is stronger evidence. Use this only if the camera will not work — your verifier will still check it with you.',
  'droom.gcap.upload.failed': 'That file could not be sent — tell your verifier on the call.',
  'droom.gcap.upload.notAllowed':
    'This document can\'t be sent as a file — your verifier will take you through it on the call.',
  'droom.gcap.upload.already':
    'That document is already on your record — your verifier can see it.',
  'droom.gcap.upload.tooBig':
    'That file is too large. Try a smaller photo, or ask your verifier for help.',
  'droom.gcap.upload.wrongType':
    'That file type cannot be read. Send a JPG or PNG photo of the document.',
  // ── testmap #13: the officer's location/IP re-check prompt (fresh capture) ─
  'droom.georecheck.title': 'Share your current location',
  'droom.georecheck.asked':
    'Your verifier asked to re-check your location and network on this call.',
  'droom.georecheck.privacy':
    'Your location and network address are recorded once as verification evidence — never tracked continuously.',
  'droom.georecheck.share': 'Share my location',
  'droom.georecheck.requesting': 'Getting a location fix…',
  'droom.georecheck.posting': 'Recording securely…',
  'droom.georecheck.done.title': 'Location shared with your verifier',
  'droom.georecheck.done.sub': 'Returning to your call…',
  'droom.georecheck.done.missing.title': 'Network details refreshed',
  'droom.georecheck.done.missing.sub':
    "Location wasn't available, and that's okay — your verifier can see your current network details and will continue with you on the call.",
  'droom.georecheck.failed': "We couldn't record that just now — please try again.",

  // ── testmap #16: side-sequenced document capture ───────────────────────────
  'droom.docside.front': 'Front of your {doc}',
  'droom.docside.back.now': 'Now the back of your {doc}',
  'droom.docside.back.optionalNote':
    'The back carries the address — add it if you can. Your verifier can also take it with you on the call.',
  'droom.gcap.doc.titleBack': 'Now show the back of your {doc}',
  'droom.gcap.side.backTitle': 'Now the back of the document',
  'droom.gcap.side.back': 'Turn the document over and hold the back steady inside the box.',
  'droom.gcap.side.front': 'Hold the front of the document steady inside the box.',

  // SPEC-13 D4: named prompts, customer flip, orientation advisory.
  'droom.gcap.doc.titleTyped': 'Show your {doc} to the camera',
  'droom.gcap.flip': 'Switch camera',
  'droom.gcap.rotate':
    'This document photographs best sideways — rotate your phone to landscape.',

  // ── SCREEN · CONFIRM YOUR DETAILS (SPEC-14 §5 golden-source intake) ────────
  'droom.confirm.subtitle': 'Your details',
  'droom.confirm.aria': 'Confirm your details',
  'droom.confirm.title': 'Confirm your details',
  // Item 14: an absent golden value read as a bare em-dash, which a customer cannot tell apart from
  // a rendering fault. Say what it means instead.
  'droom.confirm.notProvided': 'Not provided',
  'droom.confirm.sub':
    'These details were provided when your verification was set up — your identity is checked against them. Please look through each one.',
  'droom.confirm.loading': 'Loading your details…',
  'droom.confirm.readonlyNote':
    "If something here is wrong, tell your verification officer on the call — these details can't be edited here.",
  'droom.confirm.cta': 'These details are correct',
  'droom.confirm.submitting': 'Recording your confirmation…',
  'droom.confirm.err': "We couldn't record your confirmation just now. Please try again.",

  // ── SCREEN · COMPLETE ──────────────────────────────────────────────────────
  // The customer's PART is done; the VERIFICATION is not. This screen used to say
  // "You're verified" under a green Verified pill while its own Status line read
  // SUBMITTED FOR REVIEW, two inches below. At that moment the officer has STAGED a
  // decision and a second reviewer must still concur (four-eyes) — no verification
  // outcome exists. Telling a customer they are verified is a false statement of a
  // regulatory outcome on a regulated journey, and it is the one sentence they will
  // quote back if the review later rejects them.
  //
  // The reassurance is kept — they did everything right and need to know that — and it
  // is now consistent with the two bullets underneath, which already said the team
  // still reviews and activates. Nothing on the screen contradicts anything else now.
  'droom.complete.subtitle': 'Submitted',
  'droom.complete.title': "All done — thank you",
  'droom.complete.sub':
    "Thanks, {name}. You've finished your part of the video-KYC. Our team reviews it next.",
  'droom.complete.nameFallback': 'there',
  // Owner round 2026-08-28 item 1 — the downloadable PDF of this session.
  //
  // CD33 A-05: RE-WORDED. The owner asked "why is the end customer getting an option to download
  // your report (PDF)". The button itself is his own 28-Aug ask and the document is customer-scoped
  // by rule (no scores, no thresholds, no engine versions, no officer names, no decision) — but the
  // LABEL was wrong, and wrong in the way that matters. This screen's own status line says
  // SUBMITTED FOR REVIEW; "your report" on the same screen promises a verification RESULT the
  // customer has not been given and will not find inside. What the PDF actually is, is a receipt:
  // what they submitted, when, and what happens next. The label now says that, so the document and
  // the button make the same claim.
  'droom.complete.download': 'Download your submission receipt (PDF)',
  'droom.complete.downloadNote':
    'What you submitted and when — not a decision. The outcome comes from {tenantName}.',
  'droom.complete.k.reference': 'Reference',
  'droom.complete.k.completed': 'Completed',
  'droom.complete.k.status': 'Status',
  'droom.complete.v.submitted': 'SUBMITTED FOR REVIEW',
  'droom.complete.next.review':
    'Our team reviews & **activates your account** — usually within a few hours.',
  'droom.complete.next.confirm': "You'll get a **confirmation on email & SMS.**",
  'droom.complete.redirect.cta': 'Continue to {label}',
  'droom.complete.redirect.countdown': 'Taking you back to {label} in {s}s…',
  'droom.complete.redirect.now': 'Returning you to {label}…',
  // CD33 A-05: this button used to read "Return to {tenantName}" and call reset() — it named a
  // destination and went nowhere, because no hand-back is configured. A button that names a place
  // must go there; when there is nowhere to go it must not pretend. This is the honest wording for
  // the SHOW_PAGE branch. The naming button is now the REDIRECT branch's, which really does travel.
  'droom.complete.return': 'Close this page',
  'droom.complete.recorded':
    'This session was recorded securely for compliance · **Secured by KycHub**',

  // ── media hook strings (consent/quality) ───────────────────────────────────
  'droom.consenthook.loadfail': 'We could not load the consent notice. Please try again.',
  'droom.consenthook.updated': 'The consent notice was updated. Please review and confirm again.',
  'droom.consenthook.recordfail': 'Your consent could not be recorded right now. Please try again.',
  /*
   * ITEM (owner, 2026-08-25): this copy used to end "use a device with a better camera", which on a
   * phone was both wrong and insulting — the phone had the better camera, and the low resolution was
   * the video call's, not the device's. The capture now asks the camera for a still-grade frame
   * before judging it, so if this message still appears the honest advice is about FRAMING (fill the
   * frame, move closer), which the customer can actually act on, and then the upload fallback.
   */
  'droom.quality.small.doc':
    'That photo came out too small to read the document. Move closer so the card fills the frame, and try again.',
  'droom.quality.small.selfie':
    'That photo came out too small. Hold the phone a little closer and try again.',
  'droom.quality.dark': 'Too dark — move somewhere brighter and try again.',
  'droom.quality.bright': 'Too bright — move away from direct light or glare and try again.',
  'droom.quality.blurry': 'Too blurry — hold steady and try again.',
  'droom.quality.unreadable': 'That photo could not be processed — please try again.',
} as const;

export type FlowCopyKey = keyof typeof FLOW_COPY;
